import React, { useState, useEffect } from "react";
import { Divider, IconButton, Typography } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import CloseIcon from "@material-ui/icons/Close";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import { Theme } from "@material-ui/core/styles";
import { createStyles } from "@material-ui/core/styles";
import LensOutlinedIcon from "@material-ui/icons/LensOutlined";
import ViewStream from "@material-ui/icons/ViewStream";
import BorderAllOutlinedIcon from "@material-ui/icons/BorderAllOutlined";
import clsx from "clsx";
import LoadingButton from "../../../../Utils/Custom/LoadingButton";
import { primaryColor } from "../../../../../assets/theme/palette";
import NotesIcon from "@material-ui/icons/Notes";
import FilterListIcon from "@material-ui/icons/FilterList";

export enum FISHEYE_VIEW_TYPES {
  SINGLE = 0,
  PERIMETER,
  QUAD,
  E_PTZ,
}

export enum FISHEYE_MOUNTING {
  WALL = 1,
  CEILING,
  DEFAULT,
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: "15px",
    },
    form: {
      width: "100%",
      marginTop: theme.spacing(1),
    },
    submit: {
      margin: theme.spacing(3, 0, 2),
    },
    gridButton: {
      display: "flex",
      justifyContent: "flex-end",
      width: "100%",
    },
    icon: {
      fontSize: 50,
      cursor: "pointer",
      "&:hover": {
        color: primaryColor,
      },
    },
    selected: {
      color: primaryColor,
    },
    field: {
      display: "flex",
      justifyContent: "space-around",
      alignItems: "center",
    },
    field1: {
      display: "flex",
      justifyContent: "space-evenly",
      alignItems: "center",
    },
    divider: {
      marginTop: theme.spacing(2),
    },
  })
);
interface Props {
  onClose: () => void;
  onSuccess: (newValue: number, mounting: number) => void;
  currentDisplay: FISHEYE_VIEW_TYPES;
  currentMounting: FISHEYE_MOUNTING;
}

const FishEyeGridSettings = ({ onClose, onSuccess, currentDisplay, currentMounting }: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [selectedViewIndex, setSelectedViewIndex] = useState(0);
  const [selectedMounting, setSelectedMounting] = useState(0);
  const viewIndexIsSelected = (viewIndex: number) => {
    return viewIndex === selectedViewIndex;
  };

  const mountingIsSelected = (mount: number) => {
    return mount === selectedMounting;
  };

  useEffect(() => {
    setSelectedViewIndex(currentDisplay);
    setSelectedMounting(currentMounting ? currentMounting : FISHEYE_MOUNTING.WALL);
  }, []);

  return (
    <Grid container className={classes.root}>
      <Grid container justify="space-between" alignItems="center">
        <Grid item>
          <Typography variant="h6">{t("ViewOptions")}</Typography>
        </Grid>
        <Grid item>
          <IconButton onClick={() => onClose()}>
            <CloseIcon />
          </IconButton>
        </Grid>
      </Grid>

      <Grid className={classes.form}>
        <Grid item xs={12} className={classes.field}>
          <LensOutlinedIcon
            className={clsx(classes.icon, {
              [classes.selected]: viewIndexIsSelected(FISHEYE_VIEW_TYPES.SINGLE),
            })}
            onClick={() => setSelectedViewIndex(FISHEYE_VIEW_TYPES.SINGLE)}
          />
          <ViewStream
            className={clsx(classes.icon, {
              [classes.selected]: viewIndexIsSelected(FISHEYE_VIEW_TYPES.PERIMETER),
            })}
            onClick={() => setSelectedViewIndex(FISHEYE_VIEW_TYPES.PERIMETER)}
          />
          <BorderAllOutlinedIcon
            className={clsx(classes.icon, {
              [classes.selected]: viewIndexIsSelected(FISHEYE_VIEW_TYPES.QUAD),
            })}
            onClick={() => setSelectedViewIndex(FISHEYE_VIEW_TYPES.QUAD)}
          />
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={12}>
          <Divider orientation="horizontal" className={classes.divider} />
        </Grid>
      </Grid>
      <Grid container justify="space-between" alignItems="center">
        <Grid item>
          <Typography variant="h6">{t("MountingOptions")}</Typography>
        </Grid>
      </Grid>
      <Grid className={classes.form}>
        <Grid item xs={12} className={classes.field1}>
          <NotesIcon
            className={clsx(classes.icon, {
              [classes.selected]: mountingIsSelected(FISHEYE_MOUNTING.WALL),
            })}
            onClick={() => setSelectedMounting(FISHEYE_MOUNTING.WALL)}
            titleAccess={t("wall")}
          />
          <FilterListIcon
            className={clsx(classes.icon, {
              [classes.selected]: mountingIsSelected(FISHEYE_MOUNTING.CEILING),
            })}
            onClick={() => setSelectedMounting(FISHEYE_MOUNTING.CEILING)}
            titleAccess={t("ceiling")}
          />
        </Grid>
      </Grid>
      <Grid item className={classes.gridButton}>
        <LoadingButton
          type="submit"
          variant="contained"
          color="primary"
          className={classes.submit}
          onClick={() => onSuccess(selectedViewIndex, selectedMounting)}
        >
          {t("Save")}
        </LoadingButton>
      </Grid>
    </Grid>
  );
};
export default FishEyeGridSettings;
